// Authoring options for the HTTP response-validation rule builder (#6022). The predicate `field`
// is a bare `self.*` scope path over the executor's published projections (see
// HttpServiceModel.PublishedProjections); operators mirror the domain PredicateOperator enum.
import {
  ActionType,
  Combinator,
  PredicateOperator,
  type ConditionGroup,
  type PredicateLeaf,
  type ValidatorRule,
} from '@/types'
import type { ScopeNode } from '@/types/bbel-types'
import type { ServiceOutputSample } from '@/queries/output-samples'
import { headerShapeNodes, outputShapeNodes } from '@/views/builder/components/bbel/bbel-scope-output'

export interface FieldSuggestion {
  path: string
  hint: string
}

/** The fixed `self.*` roots a rule can test — always offered. Authors may type a deeper path
 *  (e.g. `self.output.items.length`); the field is a free string. */
export const selfRoots: FieldSuggestion[] = [
  { path: 'self.statusCode', hint: 'HTTP status code' },
  { path: 'self.output', hint: 'parsed response body' },
  { path: 'self.headers', hint: 'response headers' },
  { path: 'self.raw', hint: 'raw response text' },
]

/** The roots plus this service's real `self.output.*` / `self.headers.*` shape, flattened from its
 *  latest output sample (#6022) — deep field suggestions the author can pick instead of typing. */
export function selfFieldSuggestions(sample: ServiceOutputSample | undefined): FieldSuggestion[] {
  if (!sample) return selfRoots
  return [
    ...selfRoots,
    ...flattenNodes(outputShapeNodes(sample.sampleJson, 'self.output')),
    ...flattenNodes(headerShapeNodes(sample.headers ?? {}, 'self.headers')),
  ]
}

function flattenNodes(nodes: ScopeNode[]): FieldSuggestion[] {
  return nodes.flatMap((n) => [
    { path: n.path, hint: n.hint ?? '' },
    ...(n.children ? flattenNodes(n.children) : []),
  ])
}

export const operatorOptions: Array<{ value: PredicateOperator; label: string; needsValue: boolean }> = [
  { value: PredicateOperator.Equals, label: '=', needsValue: true },
  { value: PredicateOperator.NotEquals, label: '≠', needsValue: true },
  { value: PredicateOperator.GreaterThan, label: '>', needsValue: true },
  { value: PredicateOperator.GreaterThanOrEqual, label: '≥', needsValue: true },
  { value: PredicateOperator.LessThan, label: '<', needsValue: true },
  { value: PredicateOperator.LessThanOrEqual, label: '≤', needsValue: true },
  { value: PredicateOperator.Contains, label: 'contains', needsValue: true },
  { value: PredicateOperator.NotContains, label: 'does not contain', needsValue: true },
  { value: PredicateOperator.StartsWith, label: 'starts with', needsValue: true },
  { value: PredicateOperator.EndsWith, label: 'ends with', needsValue: true },
  { value: PredicateOperator.In, label: 'in', needsValue: true },
  { value: PredicateOperator.NotIn, label: 'not in', needsValue: true },
  { value: PredicateOperator.IsEmpty, label: 'is empty', needsValue: false },
  { value: PredicateOperator.IsNotEmpty, label: 'is not empty', needsValue: false },
  // Parse-free presence checks — gate on whether the response body parsed (see RegardHttpErrorAsFatal note).
  { value: PredicateOperator.IsValidJson, label: 'is valid JSON', needsValue: false },
  { value: PredicateOperator.IsNotValidJson, label: 'is not valid JSON', needsValue: false },
]

const valuelessOperators = new Set(
  operatorOptions.filter((o) => !o.needsValue).map((o) => o.value),
)
export const operatorNeedsValue = (op: PredicateOperator) => !valuelessOperators.has(op)

/** Actions and whether each surfaces a message / retry-count field. Order is the dropdown order. */
export const actionOptions: Array<{ value: ActionType; label: string }> = [
  { value: ActionType.Fail, label: 'Fail the transaction' },
  { value: ActionType.Warn, label: 'Warn (continue as success)' },
  { value: ActionType.Retry, label: 'Retry' },
  { value: ActionType.Proceed, label: 'Proceed (override a failure)' },
]

export const actionHasMessage = (t: ActionType) => t === ActionType.Fail || t === ActionType.Warn
export const actionHasRetries = (t: ActionType) => t === ActionType.Retry

let counter = 0
const localId = () => `rule_${Date.now().toString(36)}_${counter++}`

export function newPredicate(): PredicateLeaf {
  return { $type: 'predicate', field: 'self.statusCode', operator: PredicateOperator.Equals, value: '' }
}

export function newRule(): ValidatorRule {
  return {
    id: localId(),
    name: 'New validator',
    condition: { $type: 'group', combinator: Combinator.All, rules: [newPredicate()] },
    action: { type: ActionType.Fail, message: '', maxRetries: 3, retryIntervalSeconds: null },
  }
}

/** The builder edits each rule as a single ALL/ANY group of predicate rows. A rule loaded with a bare
 *  predicate is wrapped into a group; a raw-expression condition can't be represented as rows and is
 *  left for the off-UI escape hatch — {@link editableGroup} returns null so the card shows it read-only. */
export function editableGroup(rule: ValidatorRule): ConditionGroup | null {
  const c = rule.condition
  if (c.$type === 'group') return c
  if (c.$type === 'predicate') return { $type: 'group', combinator: Combinator.All, rules: [c] }
  return null
}

/** True when a rule's condition is raw BBEL the row-builder can't edit (preserved, shown read-only). */
export function isAdvancedExpression(rule: ValidatorRule): boolean {
  return rule.condition.$type === 'expression' && rule.condition.expression.trim() !== ''
}
